import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CButton, CForm, CAlert } from "@coreui/react";
import logoImg from "../../assets/Images/White_Logo.svg";
import { BASE_URL } from "../constant_api";

const OTP_LENGTH = 6;
const OTP_EXPIRY_SECONDS = 5 * 60; // 5 minutes
const RESEND_COOLDOWN_SECONDS = 30;

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const inputRefs = useRef([]);

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [otpExpiresAt, setOtpExpiresAt] = useState(
    () => Date.now() + OTP_EXPIRY_SECONDS * 1000,
  );
  const [remainingSeconds, setRemainingSeconds] = useState(OTP_EXPIRY_SECONDS);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  // ── OTP expiry countdown ──────────────────────────────────────────────────
  useEffect(() => {
    if (!email) return;

    const tick = () => {
      const secs = Math.max(0, Math.ceil((otpExpiresAt - Date.now()) / 1000));
      setRemainingSeconds(secs);
      if (secs <= 0) {
        setErrorMessage("Your verification code has expired. Please request a new one.");
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [email, otpExpiresAt]);

  // ── Resend cooldown countdown ─────────────────────────────────────────────
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const digit = value.slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    setErrorMessage("");

    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (event) => {
    event.preventDefault();

    const pastedOtp = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);

    if (!pastedOtp) return;

    const newOtp = Array(OTP_LENGTH)
      .fill("")
      .map((_, index) => pastedOtp[index] || "");

    setOtp(newOtp);
    setErrorMessage("");

    const focusIndex = Math.min(pastedOtp.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerify = async (event) => {
    event.preventDefault();

    setErrorMessage("");
    setSuccessMessage("");

    if (remainingSeconds <= 0) {
      setErrorMessage("Your verification code has expired. Please request a new one.");
      return;
    }

    const enteredOtp = otp.join("");

    if (enteredOtp.length !== OTP_LENGTH) {
      setErrorMessage(`Please enter the ${OTP_LENGTH}-digit verification code.`);
      return;
    }

    if (!email) {
      setErrorMessage("Email address is missing.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/user/verify-email-otp`,
        {
          EmailAddress: email.trim().toLowerCase(),
          OTP: enteredOtp,
        }
      );

      if (!response.data?.success) {
        setErrorMessage(response.data?.message || "Unable to verify email.");
        return;
      }

      const userData = response.data?.data || {};
      const token = userData?.token;

      if (!token) {
        setErrorMessage("Email verified, but authentication token was not received.");
        return;
      }

      localStorage.setItem("userDetail", JSON.stringify(response.data.data));
      localStorage.setItem("currentLoginUserId", JSON.stringify(response.data.data.UserID));
      localStorage.setItem("OrganisationID", "");
      setSuccessMessage(response.data?.message || "Email verified successfully.");

      setTimeout(() => {
        navigate("/query", { replace: true });
      }, 800);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Invalid verification code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resendLoading || !email) return;

    setResendLoading(true);
    setErrorMessage("");

    try {
      const response = await axios.post(`${BASE_URL}/user/resend-email-otp`, {
        EmailAddress: email.trim().toLowerCase(),
      });

      const expiresInSeconds = response.data?.expiresIn ?? OTP_EXPIRY_SECONDS;
      setOtpExpiresAt(Date.now() + expiresInSeconds * 1000);
      setOtp(Array(OTP_LENGTH).fill(""));
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setSuccessMessage("A new verification code has been emailed to you.");
      inputRefs.current[0]?.focus();
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Could not resend the code. Please try again."
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="qq-login-page">
      <style>{`
        .qq-login-page {
          min-height: 100vh;
          display: flex;
          font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }

        /* ───────── Left panel (shared brand panel) ───────── */
        .qq-left {
  flex: 1 1 50%;
  width: 50%;
  position: relative;

  background-color: #023349;
  background-image: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.06) 1px,
    transparent 1px
  );
  background-size: 22px 22px;

  color: #e7e8ee;

  padding: 56px 64px;

  display: flex;
  flex-direction: column;

  /* Center content vertically */
  justify-content: center;

  /* Keep content aligned to the left */
  align-items: flex-start;

  min-width: 0;
  box-sizing: border-box;
}

        .qq-brand {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .qq-logo {
          height: 28px;
          width: auto;
          display: block;
        }

        .qq-version-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 14px;
          padding: 5px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.09);
          font-size: 12px;
          color: #a7a9bd;
          width: fit-content;
        }

        .qq-version-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #35d07f;
        }

        .qq-headline {
          margin-top: 40px;
          font-size: 38px;
          line-height: 1.18;
          font-weight: 600;
          color: #f7f7fb;
          max-width: 460px;
          letter-spacing: -0.01em;
        }

        .qq-subtext {
          margin-top: 14px;
          font-size: 14.5px;
          line-height: 1.6;
          color: #9698ad;
          max-width: 420px;
        }

        .qq-card {
          margin-top: 36px;
          background: rgba(255,255,255,0.035);
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 14px;
          padding: 18px 20px;
          max-width: 440px;
        }

        .qq-card-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 13px;
          color: #c7c8d8;
          font-weight: 500;
        }

        .qq-card-head-left { display: flex; align-items: center; gap: 8px; }

        .qq-online-badge {
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.03em;
          color: #35d07f;
          background: rgba(53,208,127,0.12);
          padding: 3px 9px;
          border-radius: 999px;
        }

        .qq-card-stats { display: flex; gap: 36px; margin-top: 16px; }
        .qq-card-stat-label { font-size: 10.5px; color: #7c7e93; letter-spacing: 0.02em; }
        .qq-card-stat-value { margin-top: 4px; font-size: 19px; font-weight: 600; color: #f2f2f7; }
        .qq-card-stat-value.qq-green { color: #3fdc8e; }

        .qq-footer-stats { display: flex; gap: 48px; margin-top: 44px; }
        .qq-footer-stat-value { font-size: 21px; font-weight: 600; color: #f2f2f7; }
        .qq-footer-stat-label { margin-top: 4px; font-size: 11px; color: #7c7e93; letter-spacing: 0.01em; }

        /* ───────── Right panel ───────── */
        .qq-right {
          flex: 1 1 50%;
          width: 50%;
          background: #e4e4e7;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          min-width: 0;
          box-sizing: border-box;
        }

        .qq-auth-card {
          width: 100%;
          max-width: 420px;
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 20px 45px rgba(20,20,35,0.10);
          padding: 24px 28px 20px;
        }

        .qq-auth-title { font-size: 20px; font-weight: 700; color: #16171f; margin: 0; }
        .qq-auth-subtitle { margin-top: 4px; font-size: 12.5px; color: #8b8d9c; line-height: 1.45; }

        .qq-submit-btn {
          margin-top: 14px;
          width: 100%;
          background: #0095BD;
          border: none;
          border-radius: 9px;
          padding: 10px 0;
          font-size: 14px;
          font-weight: 600;
        }
        .qq-submit-btn:hover:not(:disabled) { background: #007A9A; }
        .qq-submit-btn:disabled {
          background: #e2e3e8;
          color: #9799a8;
          cursor: not-allowed;
        }

        .qq-signup-row {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #eeeef2;
          text-align: center;
          font-size: 12.5px;
          color: #8b8d9c;
        }
        .qq-signup-link { color: #0095BD; font-weight: 600; cursor: pointer; text-decoration: none; }

        /* ── OTP step ── */
        .qq-otp-row { display: flex; gap: 8px; justify-content: center; margin-top: 18px; }
        .qq-otp-box {
          width: 42px;
          height: 50px;
          text-align: center;
          font-size: 1.2em;
          font-weight: 600;
          border-radius: 8px;
          border: 1px solid #dcdde3;
          outline: none;
          color: #23242f;
          background: #fff;
        }
        .qq-otp-box:focus { border-color: #0095BD; box-shadow: 0 0 0 3px rgba(0,149,189,0.15); }
        .qq-otp-meta { margin-top: 12px; text-align: center; font-size: 13px; color: #6b6d7d; }
        .qq-otp-link { color: #0095BD; font-weight: 600; cursor: pointer; }
        .qq-otp-muted { color: #a2a4b3; }

        /* Tablet: keep both halves, but tighten the left panel's copy/padding. */
        @media (max-width: 1080px) {
          .qq-left { padding: 40px 36px; }
          .qq-headline { font-size: 30px; max-width: none; }
          .qq-subtext { max-width: none; }
          .qq-footer-stats { gap: 28px; flex-wrap: wrap; }
        }

        /* Mobile: stack the two halves, each full width. */
        @media (max-width: 860px) {
          .qq-login-page { flex-direction: column; }
          .qq-left,
          .qq-right {
            width: 100%;
            flex: none;
          }
          .qq-left { padding: 32px 24px; }
          .qq-headline { font-size: 26px; }
          .qq-card { max-width: none; }
          .qq-footer-stats { margin-top: 28px; gap: 24px; }
          .qq-right { padding: 20px; }
          .qq-auth-card { max-width: none; padding: 22px 20px 18px; }
        }
      `}</style>

      {/* ───────── Left: brand / pitch panel ───────── */}
      <div className="qq-left">
        <div>
          <div className="qq-brand">
            <img src={logoImg} alt="QuickQuery" className="qq-logo" />
          </div>

       

          <h1 className="qq-headline">Automate your account mapping in minutes</h1>
          <p className="qq-subtext">
            Configure standard expense categories, run clean multi-factor
            compliance reporting, and synchronize seamlessly to your general
            ledger.
          </p>

          {/* <div className="qq-card">
            <div className="qq-card-head">
              <span className="qq-card-head-left">Active Integrations</span>
              <span className="qq-online-badge">ONLINE</span>
            </div>
            <div className="qq-card-stats">
              <div>
                <div className="qq-card-stat-label">MAPPED ACCOUNTS</div>
                <div className="qq-card-stat-value">14,204</div>
              </div>
              <div>
                <div className="qq-card-stat-label">SYNC ACCURACY</div>
                <div className="qq-card-stat-value qq-green">99.98%</div>
              </div>
            </div>
          </div> */}
        </div>

        {/* <div className="qq-footer-stats">
          <div>
            <div className="qq-footer-stat-value">10k+</div>
            <div className="qq-footer-stat-label">HAPPY ENTERPRISE TEAMS</div>
          </div>
          <div>
            <div className="qq-footer-stat-value">$2.4B+</div>
            <div className="qq-footer-stat-label">MAPPED TRANSACTIONS</div>
          </div>
          <div>
            <div className="qq-footer-stat-value">12 mins</div>
            <div className="qq-footer-stat-label">AVERAGE SETUP TIME</div>
          </div>
        </div> */}
      </div>

      {/* ───────── Right: form panel ───────── */}
      <div className="qq-right">
        <div className="qq-auth-card">
          {!email ? (
            <div className="text-center">
              <h2 className="qq-auth-title">Email Address Missing</h2>
              <CAlert color="warning" className="mt-3 mb-0">
                Please go back to registration and try again.
              </CAlert>
              <CButton
                className="qq-submit-btn"
                onClick={() => navigate("/register")}
              >
                Back to Register
              </CButton>
            </div>
          ) : (
            <>
              <h2 className="qq-auth-title">Verify your email</h2>
              <p className="qq-auth-subtitle">
                We sent a {OTP_LENGTH}-digit verification code to{" "}
                <strong>{email}</strong>. Enter the code to unlock your account.
              </p>

              {errorMessage && (
                <CAlert
                  color="warning"
                  dismissible
                  onClose={() => setErrorMessage("")}
                  className="mt-3 mb-0"
                >
                  {errorMessage}
                </CAlert>
              )}
              {successMessage && (
                <CAlert
                  color="success"
                  dismissible
                  onClose={() => setSuccessMessage("")}
                  className="mt-3 mb-0"
                >
                  {successMessage}
                </CAlert>
              )}

              <CForm noValidate onSubmit={handleVerify}>
                <div className="qq-otp-row" onPaste={handleOtpPaste}>
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputRefs.current[i] = el)}
                      id={`otp-${i}`}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      inputMode="numeric"
                      maxLength={1}
                      disabled={loading || remainingSeconds <= 0}
                      autoComplete={i === 0 ? "one-time-code" : "off"}
                      className="qq-otp-box"
                    />
                  ))}
                </div>

                <div className="qq-otp-meta">
                  {remainingSeconds > 0 ? (
                    <span>Code expires in <strong>{formatCountdown(remainingSeconds)}</strong></span>
                  ) : (
                    <span style={{ color: "#e55353", fontWeight: 600 }}>Code expired</span>
                  )}
                </div>

                <CButton
                  type="submit"
                  className="qq-submit-btn"
                  disabled={loading || remainingSeconds <= 0}
                >
                  {loading ? "Verifying..." : "Confirm Code"}
                </CButton>

                <div className="qq-otp-meta">
                  Didn't receive the email?{" "}
                  {resendCooldown > 0 ? (
                    <span className="qq-otp-muted">Resend in {formatCountdown(resendCooldown)}</span>
                  ) : (
                    <span className="qq-otp-link" onClick={handleResend}>
                      {resendLoading ? "Sending..." : "Resend Code"}
                    </span>
                  )}
                </div>

                <div className="qq-otp-meta">
                  Wrong details?{" "}
                  <span className="qq-otp-link" onClick={() => navigate("/register")}>
                    Back to Register
                  </span>
                </div>
              </CForm>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;